import { useCallback, useEffect, useMemo, useState } from "react";

export type PinSide = "left" | "right";

export interface ColumnPinApi {
  /** Lado en que está pinneada una columna, o undefined si no lo está. */
  getPinSide: (colKey: string) => PinSide | undefined;
  /** Fija la columna al lado dado, o la desfija con `null`. */
  setPin: (colKey: string, side: PinSide | null) => void;
  /** Keys pinneadas a la izquierda, en orden de aparición. */
  pinnedLeft: string[];
  /** Keys pinneadas a la derecha, en orden de aparición. */
  pinnedRight: string[];
  resetPins: () => void;
  hasPins: boolean;
}

/**
 * Persiste qué columnas están pinneadas y a qué lado (izquierda/derecha) por
 * tabla en localStorage. Mismo patrón que useColumnOrder / useColumnWidths
 * ("table-column-pins-${tableId}"). Sólo se conservan keys que aún existen.
 */
export function useColumnPin(
  tableId: string | undefined,
  columns: { key: string }[],
): ColumnPinApi {
  const storageKey = tableId ? `table-column-pins-${tableId}` : null;

  const [pins, setPins] = useState<Record<string, PinSide>>(() => {
    if (typeof window === "undefined" || !storageKey) return {};
    try {
      const raw = window.localStorage.getItem(storageKey);
      return raw ? (JSON.parse(raw) as Record<string, PinSide>) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    if (typeof window === "undefined" || !storageKey) {
      setPins({});
      return;
    }
    try {
      const raw = window.localStorage.getItem(storageKey);
      setPins(raw ? (JSON.parse(raw) as Record<string, PinSide>) : {});
    } catch {
      setPins({});
    }
  }, [storageKey]);

  // Solo keys que aún existen, respetando el orden de `columns`.
  const known = useMemo(() => {
    const result: Record<string, PinSide> = {};
    for (const c of columns) {
      const side = pins[c.key];
      if (side === "left" || side === "right") result[c.key] = side;
    }
    return result;
  }, [pins, columns]);

  const pinnedLeft = useMemo(
    () => columns.filter((c) => known[c.key] === "left").map((c) => c.key),
    [columns, known],
  );
  const pinnedRight = useMemo(
    () => columns.filter((c) => known[c.key] === "right").map((c) => c.key),
    [columns, known],
  );

  const persist = useCallback(
    (next: Record<string, PinSide>) => {
      setPins(next);
      if (storageKey && typeof window !== "undefined") {
        try {
          window.localStorage.setItem(storageKey, JSON.stringify(next));
        } catch {
          // ignore
        }
      }
    },
    [storageKey],
  );

  const setPin = useCallback(
    (colKey: string, side: PinSide | null) => {
      setPins((prev) => {
        const next = { ...prev };
        if (side === null) delete next[colKey];
        else next[colKey] = side;
        if (storageKey && typeof window !== "undefined") {
          try {
            window.localStorage.setItem(storageKey, JSON.stringify(next));
          } catch {
            // ignore
          }
        }
        return next;
      });
    },
    [storageKey],
  );

  const getPinSide = useCallback(
    (colKey: string): PinSide | undefined => known[colKey],
    [known],
  );

  const resetPins = useCallback(() => persist({}), [persist]);

  return {
    getPinSide,
    setPin,
    pinnedLeft,
    pinnedRight,
    resetPins,
    hasPins: pinnedLeft.length > 0 || pinnedRight.length > 0,
  };
}
